import { useCarriersQuery, useCreateBindHqMarketingCompanyMutation } from "@qw/graphql";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldInput,
  Form,
  Icon,
  useToast,
} from "@qw/ui";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { ProductMetadataDialog } from "./product-form/product-metadata-dialog";

const FormSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    phone: z.string().optional(),
    fax: z.string().optional(),
    url: z.string().optional(),
    email: z.string().email("Please enter a valid email").optional().or(z.literal("")),
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
  })
  .refine(
    (data) => {
      const hasAnyAddressField = data.line1 || data.city || data.state || data.postalCode;
      if (hasAnyAddressField) {
        return data.line1 && data.city && data.state && data.postalCode;
      }
      return true;
    },
    {
      message:
        "If providing an address, all required fields (Address Line 1, City, State, and Postal Code) must be filled in",
      path: ["line1"],
    }
  );

type FormValues = z.infer<typeof FormSchema>;

interface DialogContentProps {
  onOpenChange: (open: boolean) => void;
  savingAction: "save" | "saveAndCreate" | null;
  setSavingAction: (value: "save" | "saveAndCreate" | null) => void;
  onSaveAndCreateProduct: (carrierSlug?: string) => void;
}

const CarrierDialogContent: React.FC<DialogContentProps> = ({
  onOpenChange,
  savingAction,
  setSavingAction,
  onSaveAndCreateProduct,
}) => {
  const { toast } = useToast();
  const [createMarketingCompany] = useCreateBindHqMarketingCompanyMutation();
  const { handleSubmit } = useFormContext<FormValues>();
  const { refetch: refetchCarriers } = useCarriersQuery();

  const createCompany = async (values: FormValues) => {
    const hasAddress = values.line1 && values.city && values.state && values.postalCode;

    const result = await createMarketingCompany({
      variables: {
        input: {
          name: values.name,
          phone: values.phone || undefined,
          fax: values.fax || undefined,
          url: values.url || undefined,
          email: values.email || undefined,
          mailingAddress: hasAddress
            ? {
                line1: values.line1!,
                line2: values.line2 || undefined,
                city: values.city!,
                state: values.state!,
                postalCode: values.postalCode!,
              }
            : undefined,
        },
      },
      refetchQueries: ["BindHqMarketingCompanies", "Carriers"],
    });

    return result.data?.createBindHqMarketingCompany;
  };

  const handleSave = async () => {
    setSavingAction("save");
    try {
      await handleSubmit(async (values) => {
        await createCompany(values);
        toast({ title: "Carrier created successfully" });
        onOpenChange(false);
      })();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.error("Error creating carrier:", error);
      toast({
        title: "Error creating carrier",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setSavingAction(null);
    }
  };

  const handleSaveAndCreateProduct = async () => {
    setSavingAction("saveAndCreate");
    try {
      await handleSubmit(async (values) => {
        const marketingCompany = await createCompany(values);
        toast({ title: "Carrier created successfully" });
        onOpenChange(false);

        const { data: carriersData } = await refetchCarriers();
        const carrier = carriersData?.carriers?.find((c) => c.name === marketingCompany?.name);

        onSaveAndCreateProduct(carrier?.slug);
      })();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.error("Error creating carrier:", error);
      toast({
        title: "Error creating carrier",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setSavingAction(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldInput name="name" label="Name" placeholder="Enter company name" />
      <FieldInput name="url" label="Website" placeholder="Website URL" />

      <Collapsible defaultOpen={false}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors [&[data-state=open]_.icon]:rotate-90">
          <Icon icon="chevron_right" className="h-4 w-4 transition-transform" />
          Additional Information
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 flex flex-col gap-4">
          <FieldInput name="phone" label="Phone" placeholder="Phone number" />

          <FieldInput name="fax" label="Fax" placeholder="Fax number" />

          <FieldInput name="email" label="Email" placeholder="Email address" />

          <FieldInput name="line1" label="Address Line 1" placeholder="Street address" />
          <FieldInput name="line2" label="Address Line 2" placeholder="Suite, unit, etc." />
          <div className="grid grid-cols-3 gap-4">
            <FieldInput name="city" label="City" placeholder="City" />
            <FieldInput name="state" label="State" placeholder="State" />
            <FieldInput name="postalCode" label="Postal Code" placeholder="ZIP code" />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <DialogFooter className="gap-2">
        <Button type="button" variant="secondary" onClick={handleSave} disabled={savingAction !== null}>
          {savingAction === "save" ? (
            <>
              <Icon icon="sync" className="animate-spin mr-2 h-4 w-4" />
              Creating...
            </>
          ) : (
            "Create Carrier"
          )}
        </Button>
        <Button type="button" onClick={handleSaveAndCreateProduct} disabled={savingAction !== null}>
          {savingAction === "saveAndCreate" ? (
            <>
              <Icon icon="sync" className="animate-spin mr-2 h-4 w-4" />
              Creating...
            </>
          ) : (
            "Save & Create Product"
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const NewCarrierButton = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [defaultCarrierId, setDefaultCarrierId] = useState<string | undefined>();
  const [savingAction, setSavingAction] = useState<"save" | "saveAndCreate" | null>(null);

  const defaultValues: FormValues = {
    name: "",
    phone: "",
    fax: "",
    url: "",
    email: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
  };

  const handleSaveAndCreateProduct = (carrierSlug?: string) => {
    setDefaultCarrierId(carrierSlug);
    setProductDialogOpen(true);
  };

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setDialogOpen(true)} className="border">
        <Icon icon="add" /> New Carrier
      </Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Carrier</DialogTitle>
          </DialogHeader>

          <Form mode="onChange" onSubmit={async () => {}} defaultValues={defaultValues} validationSchema={FormSchema}>
            <CarrierDialogContent
              onOpenChange={setDialogOpen}
              savingAction={savingAction}
              setSavingAction={setSavingAction}
              onSaveAndCreateProduct={handleSaveAndCreateProduct}
            />
          </Form>
        </DialogContent>
      </Dialog>
      <ProductMetadataDialog
        open={productDialogOpen}
        onOpenChange={setProductDialogOpen}
        defaultCarrierId={defaultCarrierId}
      />
    </>
  );
};
